import { dayKeyOf, toDate } from "./agenda-schedule";
import type { AgendaItem } from "./schemas";

/**
 * What the now-and-next card on the attendee overview shows at a moment.
 *
 * Pure, so the card only has to re-run it as the clock moves (see
 * `NowNextCard`), and so every phase can be tested at a fixed instant.
 *
 * WHOSE SESSIONS. Everybody's. The card never leaves a session out, because
 * an organiser may put the keynote and the closing in a track of their own
 * ("Main Programme") as readily as leave them untracked, and a card that
 * dropped other tracks would say "Next session at 11:00" while the keynote
 * runs. What the attendee's track changes
 * is the order: among sessions running together, or starting together, theirs
 * come first and are marked `mine`. Whatever does not fit is counted in
 * `moreNow` and `moreNext`, so the card can say there is more.
 *
 * WHICH DAY. "Today" is the event's day on the event's clock, the same day
 * key the agenda tab groups by (`dayKeyOf`), so the card and the tab never
 * disagree about which day a session is on.
 *
 * A session with no end time is treated as a moment: it is next until it
 * starts and then it is over. The rules require `endsAt`, so this only
 * covers a half-written document.
 */

export type NowNextSession = {
  item: AgendaItem;
  start: Date;
  end: Date | null;
  /** In one of the attendee's own tracks. */
  mine: boolean;
  /** YYYY-MM-DD of the start on the event's clock. */
  dayKey: string;
};

/**
 * `moreNext` counts the sessions not shown that start no later than the last
 * one shown: the rest of a parallel slot, which is what "+N more" is about.
 * Sessions later in the day are not "more", they are just later.
 */
type Upcoming = { next: NowNextSession[]; moreNext: number };

export type NowNext =
  /** Nothing has started yet. `next` opens the event. */
  | ({ phase: "before" } & Upcoming)
  /** Something is running. `next` may be empty near the end. */
  | ({ phase: "live"; now: NowNextSession[]; moreNow: number } & Upcoming)
  /** A gap in the day: lunch, a break, the evening before day two. */
  | ({ phase: "break" } & Upcoming)
  /** The last session has ended today. Tomorrow the card goes away. */
  | { phase: "finished" };

/** Sessions shown under "Now" and under "Next", each. */
export const NOW_NEXT_LIMIT = 2;

/**
 * In the attendee's track, or in no track at all (a plenary). What the
 * agenda's "My track" chip lists. The now-and-next card does not filter.
 */
export function isForAttendee(
  item: AgendaItem,
  myTrackIds: ReadonlySet<string>,
): boolean {
  const trackIds = item.trackIds ?? [];
  return trackIds.length === 0 || trackIds.some((id) => myTrackIds.has(id));
}

/**
 * The now-and-next state at `now`, or null when the card has nothing to say:
 * no timed sessions, or the event finished on an earlier day.
 */
export function selectNowNext(
  items: AgendaItem[],
  now: Date,
  myTrackIds: ReadonlySet<string>,
  /** The event's IANA zone, which decides what "today" is. */
  timeZone: string,
): NowNext | null {
  const nowMs = now.getTime();
  const all = items.flatMap((item): NowNextSession[] => {
    const start = toDate(item.startsAt);
    if (!start) return [];
    const end = toDate(item.endsAt);
    return [
      {
        item,
        start,
        // An end at or before the start is a typo; read it as a moment.
        end: end && end.getTime() > start.getTime() ? end : null,
        mine: (item.trackIds ?? []).some((id) => myTrackIds.has(id)),
        dayKey: dayKeyOf(start, timeZone),
      },
    ];
  });
  if (all.length === 0) return null;

  const current = all
    .filter(
      (session) =>
        session.start.getTime() <= nowMs &&
        session.end !== null &&
        session.end.getTime() > nowMs,
    )
    .sort(byRelevance);
  const upcoming = all
    .filter((session) => session.start.getTime() > nowMs)
    .sort(byRelevance)
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  const next = upcoming.slice(0, NOW_NEXT_LIMIT);
  const lastShownStart = next.at(-1)?.start.getTime() ?? -Infinity;
  const moreNext =
    upcoming.filter((session) => session.start.getTime() <= lastShownStart)
      .length - next.length;

  if (current.length > 0) {
    const now = current.slice(0, NOW_NEXT_LIMIT);
    return {
      phase: "live",
      now,
      moreNow: current.length - now.length,
      next,
      moreNext,
    };
  }

  const started = all.some((session) => session.start.getTime() <= nowMs);
  if (next.length > 0)
    return { phase: started ? "break" : "before", next, moreNext };

  const lastEnd = Math.max(
    ...all.map((session) => (session.end ?? session.start).getTime()),
  );
  return dayKeyOf(new Date(lastEnd), timeZone) === dayKeyOf(now, timeZone)
    ? { phase: "finished" }
    : null;
}

/**
 * The attendee's own sessions before everybody else's, then earlier first, then the
 * shorter one, then by title, so two parallel sessions always come out in the
 * same order however the snapshot arrived. `Array.prototype.sort` is stable,
 * so sorting upcoming sessions by start afterwards keeps this order within a
 * start time.
 */
function byRelevance(a: NowNextSession, b: NowNextSession): number {
  return (
    Number(b.mine) - Number(a.mine) ||
    a.start.getTime() - b.start.getTime() ||
    (a.end?.getTime() ?? Infinity) - (b.end?.getTime() ?? Infinity) ||
    a.item.title.localeCompare(b.item.title)
  );
}
